(function (root) {
  'use strict';

  const RadarHeader = {
    // Clip insets (px) that frame one menu item inside the list: the item's
    // distance from the list's left edge and from the list's right edge.
    inkInsets(itemLeft, itemRight, listLeft, listRight) {
      return { left: itemLeft - listLeft, right: listRight - itemRight };
    },

    // Which way the highlight travels between two left insets ('' = first placement or no move).
    inkDirection(fromLeft, toLeft) {
      if (fromLeft === null || fromLeft === toLeft) return '';
      return toLeft > fromLeft ? 'right' : 'left';
    },

    // Offset (SVG user units) that moves an eye glint toward the pointer; the full
    // `maxOffset` is reached when the pointer is `reach` px or farther from the eye.
    glintOffset(eyeX, eyeY, pointerX, pointerY, maxOffset, reach) {
      const dx = pointerX - eyeX;
      const dy = pointerY - eyeY;
      const distance = Math.hypot(dx, dy);
      if (distance === 0 || !(reach > 0)) return { x: 0, y: 0 };
      const scale = (Math.min(1, distance / reach) * maxOffset) / distance;
      return { x: dx * scale, y: dy * scale };
    },

    lerp(from, to, amount) {
      return from + (to - from) * amount;
    },

    randomBetween(min, max, random) {
      return min + (max - min) * random;
    },

    // Does a currency row match what was typed? `searchData` is the row's data-search string
    // (code, currency name and the names of the countries that use it, from Liquid). An empty
    // query keeps every row.
    localeMatches(searchData, query) {
      const needle = String(query == null ? '' : query).trim().toLowerCase();
      if (needle === '') return true;
      return String(searchData == null ? '' : searchData).toLowerCase().includes(needle);
    },

    // Label of the trigger: "EN · EUR €". Either half is dropped when its switcher is off, and a
    // symbol that only repeats the code (Shopify gives CHF the symbol "CHF") is left out.
    localeLabel(languageCode, currencyCode, symbol) {
      const parts = [];
      if (languageCode) parts.push(String(languageCode).toUpperCase());
      if (currencyCode) {
        const code = String(currencyCode).toUpperCase();
        const sign = symbol ? String(symbol).trim() : '';
        parts.push(sign && sign.toUpperCase() !== code ? `${code} ${sign}` : code);
      }
      return parts.join(' · ');
    },
  };

  if (typeof module === 'object' && module.exports) module.exports = RadarHeader;
  if (typeof document === 'undefined' || typeof customElements === 'undefined') return;

  const reducedMotion = root.matchMedia('(prefers-reduced-motion: reduce)');

  // Mint pill behind the desktop menu: rests on the current page's item and slides,
  // stretching then catching up, to the item under the pointer or keyboard focus.
  class RadarNavInk extends HTMLElement {
    connectedCallback() {
      this.menu = this.closest('full-menu');
      this.list = this.menu ? this.menu.querySelector(':scope > .thb-full-menu') : null;
      if (!this.list) return;
      this.items = Array.from(this.list.children);
      this.left = null;
      this.current = null;
      this.onEnter = (event) => this.moveTo(event.currentTarget, true);
      this.onLeave = () => this.rest();
      this.onFocusOut = (event) => {
        if (!this.menu.contains(event.relatedTarget)) this.rest();
      };
      this.items.forEach((item) => {
        item.addEventListener('pointerenter', this.onEnter);
        item.addEventListener('focusin', this.onEnter);
      });
      this.menu.addEventListener('pointerleave', this.onLeave);
      this.menu.addEventListener('focusout', this.onFocusOut);
      this.resizeObserver = new ResizeObserver(() => this.snap());
      this.resizeObserver.observe(this.list);
      if (document.fonts) document.fonts.ready.then(() => this.snap());
      this.snap();
    }

    disconnectedCallback() {
      if (!this.items) return;
      this.items.forEach((item) => {
        item.removeEventListener('pointerenter', this.onEnter);
        item.removeEventListener('focusin', this.onEnter);
      });
      this.menu.removeEventListener('pointerleave', this.onLeave);
      this.menu.removeEventListener('focusout', this.onFocusOut);
      this.resizeObserver.disconnect();
    }

    activeItem() {
      return this.items.find((item) => item.classList.contains('active')) || null;
    }

    // Re-places the pill without animation (load, resize, fonts loaded).
    snap() {
      const item = this.current || this.activeItem();
      if (item) this.moveTo(item, false);
    }

    rest() {
      const item = this.activeItem();
      if (item) {
        this.moveTo(item, true);
      } else {
        this.current = null;
        this.classList.remove('is-visible');
      }
    }

    moveTo(item, animate) {
      const listRect = this.list.getBoundingClientRect();
      if (listRect.width === 0) return; // desktop menu hidden
      const itemRect = item.getBoundingClientRect();
      const insets = RadarHeader.inkInsets(itemRect.left, itemRect.right, listRect.left, listRect.right);
      const instant = !animate || !this.classList.contains('is-visible') || reducedMotion.matches;
      this.dataset.direction = instant ? '' : RadarHeader.inkDirection(this.left, insets.left);
      this.classList.toggle('is-instant', instant);
      this.style.setProperty('--radar-ink-l', `${insets.left}px`);
      this.style.setProperty('--radar-ink-r', `${insets.right}px`);
      this.left = insets.left;
      this.current = item;
      this.classList.add('is-visible');
    }
  }

  const GLINT_MAX = 24; // SVG user units inside an eye (eyes are about 112 x 102)
  const GLINT_REACH = 400; // px from the eye for a full look
  const IDLE_AFTER = 4000; // ms without pointer input before looking around

  // Alien of the live logo: the eye glints follow the pointer (or the finger),
  // look around when idle and blink now and then.
  class RadarAlien extends HTMLElement {
    connectedCallback() {
      this.svg = this.querySelector('.radar-logo__svg');
      if (!this.svg || reducedMotion.matches) return;
      this.eyes = ['l', 'r'].map((side) => {
        const lids = Array.from(this.svg.querySelectorAll(`.radar-logo__lid[data-eye="${side}"]`));
        return {
          cy: Number(this.svg.getAttribute(`data-eye-${side}`).split(' ')[1]),
          lids,
          shape: lids[0],
          glint: this.svg.querySelector(`.radar-logo__lid[data-eye="${side}"] .radar-logo__glint`),
          x: 0,
          y: 0,
          tx: 0,
          ty: 0,
        };
      });
      this.lastPointer = 0;
      this.frame = null;
      this.timers = new Set();
      // Pointer events can fire several times per frame: keep only the latest position and
      // aim at it in the next animation frame (one layout read per frame at most).
      this.aimPending = false;
      this.aimX = 0;
      this.aimY = 0;
      this.onPointer = (event) => {
        this.lastPointer = performance.now();
        this.aimX = event.clientX;
        this.aimY = event.clientY;
        this.aimPending = true;
        this.animate();
      };
      this.onVisibility = () => (document.hidden ? this.pause() : this.resume());
      root.addEventListener('pointermove', this.onPointer, { passive: true });
      root.addEventListener('pointerdown', this.onPointer, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.resume();
    }

    disconnectedCallback() {
      if (!this.eyes) return;
      this.pause();
      root.removeEventListener('pointermove', this.onPointer);
      root.removeEventListener('pointerdown', this.onPointer);
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    // Points the eye targets at a viewport position (reads the eyes' layout boxes).
    lookAt(clientX, clientY) {
      this.eyes.forEach((eye) => {
        const rect = eye.shape.getBoundingClientRect();
        const offset = RadarHeader.glintOffset(
          rect.left + rect.width / 2,
          rect.top + rect.height / 2,
          clientX,
          clientY,
          GLINT_MAX,
          GLINT_REACH,
        );
        eye.tx = offset.x;
        eye.ty = offset.y;
      });
    }

    animate() {
      if (this.frame !== null) return;
      const step = () => {
        if (this.aimPending) {
          this.aimPending = false;
          this.lookAt(this.aimX, this.aimY);
        }
        let moving = false;
        this.eyes.forEach((eye) => {
          eye.x = RadarHeader.lerp(eye.x, eye.tx, 0.18);
          eye.y = RadarHeader.lerp(eye.y, eye.ty, 0.18);
          if (Math.abs(eye.tx - eye.x) > 0.05 || Math.abs(eye.ty - eye.y) > 0.05) {
            moving = true;
          } else {
            eye.x = eye.tx;
            eye.y = eye.ty;
          }
          eye.glint.setAttribute('transform', `translate(${eye.x.toFixed(2)} ${eye.y.toFixed(2)})`);
        });
        this.frame = moving ? root.requestAnimationFrame(step) : null;
      };
      this.frame = root.requestAnimationFrame(step);
    }

    later(callback, delay) {
      const id = root.setTimeout(() => {
        this.timers.delete(id);
        callback();
      }, delay);
      this.timers.add(id);
    }

    scheduleGlance() {
      this.later(() => {
        if (performance.now() - this.lastPointer > IDLE_AFTER) {
          const angle = Math.random() * Math.PI * 2;
          const amount = RadarHeader.randomBetween(0.3, 1, Math.random()) * GLINT_MAX;
          this.eyes.forEach((eye) => {
            eye.tx = Math.cos(angle) * amount;
            eye.ty = Math.sin(angle) * amount;
          });
          this.animate();
        }
        this.scheduleGlance();
      }, RadarHeader.randomBetween(2000, 4000, Math.random()));
    }

    scheduleBlink() {
      this.later(() => {
        this.setLids(0.1);
        this.later(() => {
          this.setLids(1);
          this.scheduleBlink();
        }, 140);
      }, RadarHeader.randomBetween(4000, 7000, Math.random()));
    }

    // Squashes each eye (shape + clipped glint) vertically around the eye centre.
    setLids(scale) {
      this.eyes.forEach((eye) => {
        eye.lids.forEach((lid) => {
          if (scale === 1) lid.removeAttribute('transform');
          else lid.setAttribute('transform', `translate(0 ${eye.cy}) scale(1 ${scale}) translate(0 ${-eye.cy})`);
        });
      });
    }

    pause() {
      this.timers.forEach((id) => root.clearTimeout(id));
      this.timers.clear();
      if (this.frame !== null) root.cancelAnimationFrame(this.frame);
      this.frame = null;
      this.setLids(1);
    }

    resume() {
      this.pause();
      this.scheduleGlance();
      this.scheduleBlink();
    }
  }

  const PANEL_FADE = 320; // ms: must outlast the panel's closing transition in radar-brand.css

  // Language/currency panel of the header and of the phone drawer. The theme's own localization
  // <select>s stay in the form (they are what a visitor without JavaScript uses): picking a
  // language pill or a currency row sets the matching select and submits that same form, so the
  // change still goes through Shopify's localization form.
  // Una tastiera fisica c'e' dove il puntatore e' fine (mouse, trackpad, penna).
  // Su un telefono il puntatore e' grosso e la tastiera e' a schermo: alzarla
  // da soli ruba meta' pannello.
  const fineePointer = root.matchMedia ? root.matchMedia('(pointer: fine)') : { matches: true };

  class RadarLocale extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      this.trigger = this.querySelector('.radar-locale__trigger');
      this.panel = this.querySelector('.radar-locale__panel');
      if (!this.form || !this.trigger || !this.panel) return;
      this.label = this.querySelector('.radar-locale__current');
      this.search = this.querySelector('.radar-locale__input');
      this.empty = this.querySelector('.radar-locale__empty');
      this.restTitle = this.querySelector('[data-group="rest-title"]');
      this.rows = Array.from(this.querySelectorAll('.radar-locale__row'));
      this.langs = Array.from(this.querySelectorAll('.radar-locale__lang'));
      this.isOpen = false;
      this.closeTimer = null;
      this.swallowEscape = false;

      this.onClick = (event) => this.handleClick(event);
      this.onInput = () => this.filter();
      // The theme gives EVERY <button> inside #mobile-menu its sub-menu "back" handler
      // (app.js: menu.querySelectorAll('button')), which closes the whole drawer. Catching
      // the click on the way down and stopping it there keeps that handler off our buttons,
      // whatever order the two scripts ran in.
      this.addEventListener('click', this.onClick, true);
      // Same story for Escape: the theme closes the drawer on keyup, which would pull the
      // trigger we just moved the focus back to out of the page.
      this.onKeyup = (event) => {
        if (event.key === 'Escape' && this.swallowEscape) {
          this.swallowEscape = false;
          event.stopPropagation();
        }
      };
      document.addEventListener('keyup', this.onKeyup, true);
      // Only while the panel is open (removed again on close and on disconnect).
      this.onKeydown = (event) => this.keydown(event);
      this.onOutside = (event) => {
        if (!this.contains(event.target)) this.setOpen(false);
      };

      if (this.search) this.search.addEventListener('input', this.onInput);
    }

    disconnectedCallback() {
      if (!this.trigger) return;
      this.unwatch();
      if (this.closeTimer !== null) root.clearTimeout(this.closeTimer);
      this.closeTimer = null;
      this.removeEventListener('click', this.onClick, true);
      document.removeEventListener('keyup', this.onKeyup, true);
      if (this.search) this.search.removeEventListener('input', this.onInput);
    }

    unwatch() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('pointerdown', this.onOutside);
      document.removeEventListener('focusin', this.onOutside);
    }

    setOpen(state) {
      if (state === this.isOpen) return;
      this.isOpen = state;
      this.trigger.setAttribute('aria-expanded', String(state));
      if (this.closeTimer !== null) {
        root.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      if (state) {
        this.panel.hidden = false;
        // The panel is laid out first, then fades in from opacity 0 on the next frame.
        root.requestAnimationFrame(() => {
          if (this.isOpen) this.panel.classList.add('is-open');
        });
        document.addEventListener('keydown', this.onKeydown);
        document.addEventListener('pointerdown', this.onOutside);
        document.addEventListener('focusin', this.onOutside);
        // Il campo di ricerca prende il fuoco solo dove c'e' una tastiera gia'
        // in vista. Su un telefono metterlo a fuoco significa far salire la
        // tastiera addosso al pannello appena lo si apre: si cerca la valuta e
        // si trova mezzo schermo coperto, senza averlo chiesto. Chi vuole
        // cercare tocca il campo, ed e' un tocco solo.
        if (this.search && fineePointer.matches) this.search.focus({ preventScroll: true });
        return;
      }
      this.panel.classList.remove('is-open');
      this.unwatch();
      // Kept in the DOM while it fades out, then hidden again (out of the tab order).
      this.closeTimer = root.setTimeout(() => {
        this.closeTimer = null;
        if (!this.isOpen) this.panel.hidden = true;
      }, PANEL_FADE);
    }

    filter() {
      const query = this.search.value;
      let shown = 0;
      this.rows.forEach((row) => {
        const hit = RadarHeader.localeMatches(row.dataset.search, query);
        row.hidden = !hit;
        if (hit) shown += 1;
      });
      // While searching the list is one run of results: the "all currencies" rule between the
      // main codes and the rest would only split it. Both groups keep filtering their own rows,
      // so a main code (EUR…) is still found.
      if (this.restTitle) this.restTitle.hidden = query.trim() !== '';
      if (this.empty) this.empty.hidden = shown > 0;
    }

    keydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.swallowEscape = true;
        this.setOpen(false);
        this.trigger.focus();
        return;
      }
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (!this.contains(event.target)) return;
      const visible = this.rows.filter((row) => !row.hidden);
      if (visible.length === 0) return;
      event.preventDefault();
      const step = event.key === 'ArrowDown' ? 1 : -1;
      const from = visible.indexOf(document.activeElement);
      const next = from === -1 ? (step === 1 ? 0 : visible.length - 1) : (from + step + visible.length) % visible.length;
      visible[next].focus();
    }

    handleClick(event) {
      const hit = event.target.closest('.radar-locale__trigger, .radar-locale__lang, .radar-locale__row');
      if (!hit) return;
      event.stopPropagation();
      if (hit === this.trigger) {
        this.setOpen(!this.isOpen);
      } else if (hit.classList.contains('radar-locale__lang')) {
        this.apply('language_code', hit.dataset.language, this.langs, hit);
      } else {
        this.apply('currency_code', hit.dataset.currency, this.rows, hit);
      }
    }

    apply(name, value, items, chosen) {
      const select = this.form.elements[name];
      if (!select || !value) return;
      if (select.value === value) {
        this.setOpen(false);
        this.trigger.focus();
        return;
      }
      select.value = value;
      if (select.value !== value) return; // no such option: leave the page alone
      items.forEach((item) => item.classList.toggle('is-current', item === chosen));
      this.refreshLabel();
      this.setOpen(false);
      // The theme's own form (no change event: app.js submits on any change inside it).
      this.form.submit();
    }

    refreshLabel() {
      if (!this.label) return;
      const pill = this.querySelector('.radar-locale__lang.is-current');
      const row = this.querySelector('.radar-locale__row.is-current');
      this.label.textContent = RadarHeader.localeLabel(
        pill ? pill.dataset.language : '',
        row ? row.dataset.currency : '',
        row ? row.dataset.symbol : '',
      );
    }
  }

  if (!customElements.get('radar-alien')) customElements.define('radar-alien', RadarAlien);

  if (!customElements.get('radar-nav-ink')) customElements.define('radar-nav-ink', RadarNavInk);

  if (!customElements.get('radar-locale')) customElements.define('radar-locale', RadarLocale);
})(typeof window !== 'undefined' ? window : globalThis);
